/**
 * Employment Invitation oRPC Contract
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateEmploymentInvitationInputSchema, EmploymentInvitationResponseSchema, VerifyOtpInputSchema, VerifyResponseSchema, CompleteSignupInputSchema, CompleteSignupResponseSchema, EmploymentInvitationListItemSchema, } from "@hourly/api/employmentInvitation";
// ============ Create Invitation ============
export const createEmploymentInvitation = oc
    .route({
    method: "POST",
    path: "/employment-invitations",
    summary: "Create employment invitation",
    description: "Invite a user to join a company. Generates OTP and magic link.",
    tags: ["Employment Invitations"],
})
    .input(CreateEmploymentInvitationInputSchema)
    .output(EmploymentInvitationResponseSchema);
// ============ Verify OTP ============
export const verifyOtp = oc
    .route({
    method: "POST",
    path: "/employment-invitations/verify-otp",
    summary: "Verify OTP and get completion token",
    description: "Validate the 6-digit OTP code and receive a completion token.",
    tags: ["Employment Invitations"],
})
    .input(VerifyOtpInputSchema)
    .output(VerifyResponseSchema);
// ============ Verify Magic Link ============
export const verifyMagicLink = oc
    .route({
    method: "GET",
    path: "/employment-invitations/verify/{token}",
    summary: "Verify magic link and get completion token",
    description: "Validate the magic link token and receive a completion token.",
    tags: ["Employment Invitations"],
})
    .input(z.object({ token: z.string() }))
    .output(VerifyResponseSchema);
// ============ Complete Signup ============
export const completeSignup = oc
    .route({
    method: "POST",
    path: "/employment-invitations/complete",
    summary: "Complete signup with password",
    description: "Create user account and employment using the completion token.",
    tags: ["Employment Invitations"],
})
    .input(CompleteSignupInputSchema)
    .output(CompleteSignupResponseSchema);
// ============ Send Welcome Email ============
export const sendWelcomeEmail = oc
    .route({
    method: "POST",
    path: "/employment-invitations/send-welcome",
    summary: "Send welcome email with OTP to an invited user",
    description: "Manager-initiated endpoint to send a Welcome to Clockwork email with OTP to an invited user.",
    tags: ["Employment Invitations"],
})
    .input(z.object({
    userId: z.uuid(),
    firstName: z.string().min(1),
    otp: z.string().length(6),
    isMobileUser: z.boolean().optional(),
    email: z.string().email().optional(),
}))
    .output(z.object({ success: z.literal(true) }));
// ============ Resend OTP ============
export const resendOtp = oc
    .route({
    method: "POST",
    path: "/employment-invitations/resend-otp",
    summary: "Resend OTP for a pending invitation",
    description: "Public endpoint for invited users to request a new OTP code. Generates new OTP and magic link, resets expiration, and sends email.",
    tags: ["Employment Invitations"],
})
    .input(z.object({ email: z.string().email() }))
    .output(z.object({ success: z.literal(true) }));
// ============ List Invitations ============
export const listEmploymentInvitations = oc
    .route({
    method: "GET",
    path: "/employment-invitations",
    summary: "List employment invitations",
    description: "List all invitations for the authenticated user's company.",
    tags: ["Employment Invitations"],
})
    .input(z.object({
    status: z.enum(["PENDING", "USED", "EXPIRED"]).optional(),
}))
    .output(z.array(EmploymentInvitationListItemSchema));
// ============ Contract Export ============
export const employmentInvitationContract = {
    create: createEmploymentInvitation,
    verifyOtp,
    verifyMagicLink,
    complete: completeSignup,
    resendOtp,
    list: listEmploymentInvitations,
    sendWelcomeEmail,
};
//# sourceMappingURL=employmentInvitation.js.map